import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { X, Sparkles } from "lucide-react";

const MODELS = [
  "a tall female model with dark hair",
  "a male model with an athletic build",
  "a female model with curly hair and medium build",
  "a plus-size female model",
  "a young male model with short blond hair",
  "a mature female model with grey hair",
];

const SETTINGS = [
  "in a bright photo studio with seamless white backdrop",
  "on a Paris street at golden hour",
  "on a fashion runway with dramatic lighting",
  "on a Mediterranean beach at sunset",
  "in a minimalist concrete gallery space",
  "on a rainy Tokyo street at night, neon lights",
  "in an autumn park with soft natural light",
  "in a luxury hotel lobby",
];

/**
 * Remix modal: takes an existing try-on iteration and re-runs generation with different
 * model / setting / material / instructions. Uses `source_version_id` on the tryon endpoint
 * so the base image is that specific iteration, not the latest cover_image.
 */
export default function RemixModal({ open, onClose, designId, sourceVersionId, initial = {}, onRemixed }) {
  const [model, setModel] = useState(initial.model || MODELS[0]);
  const [setting, setSetting] = useState(initial.setting || SETTINGS[0]);
  const [materialId, setMaterialId] = useState(initial.material_id || "");
  const [instructions, setInstructions] = useState(initial.instructions || "");
  const [materials, setMaterials] = useState([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (open) api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) {
      setModel(initial.model || MODELS[0]);
      setSetting(initial.setting || SETTINGS[0]);
      setMaterialId(initial.material_id || "");
      setInstructions(initial.instructions || "");
    }
  }, [open, initial.model, initial.setting, initial.material_id, initial.instructions]);

  if (!open) return null;

  const remix = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/generate/tryon", {
        design_id: designId,
        source_version_id: sourceVersionId || null,
        model_desc: model,
        setting,
        material_id: materialId || null,
        instructions,
      });
      toast.success("Remixed — new iteration added");
      onRemixed?.(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setRunning(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-zinc-950/60 flex items-center justify-center p-4" data-testid="remix-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-zinc-500" />
            <h2 className="font-heading text-lg font-bold">Remix this try-on</h2>
          </div>
          <button data-testid="remix-close" onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Model</label>
            <select data-testid="remix-model" value={model} onChange={(e) => setModel(e.target.value)}
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Setting / Location</label>
            <select data-testid="remix-setting" value={setting} onChange={(e) => setSetting(e.target.value)}
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {SETTINGS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Material override (optional)</label>
            <select data-testid="remix-material" value={materialId} onChange={(e) => setMaterialId(e.target.value)}
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="">No override</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.type}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Styling notes (optional)</label>
            <textarea data-testid="remix-instructions" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. pair with white sneakers, closed pose, add sunglasses"
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md hover:bg-zinc-100">Cancel</button>
          <button data-testid="remix-submit" disabled={running} onClick={remix}
            className="inline-flex items-center gap-1.5 bg-zinc-950 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-40">
            <Sparkles size={12} /> {running ? "Remixing…" : "Remix"}
          </button>
        </div>
      </div>
    </div>
  );
}
